import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp,
  deleteDoc,
  CollectionReference,
  DocumentData,
  Firestore
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Customer } from '../types/customer';

// التحقق من توفر خدمة Firestore
const ensureFirestore = (): Firestore => {
  if (!db) {
    throw new Error('خدمة Firestore غير متوفرة');
  }
  return db;
};



// الحصول على مرجع المجموعة بشكل آمن
const getCustomersCollection = (): CollectionReference<DocumentData> => {
  const firestore = ensureFirestore();
  return collection(firestore, 'customers');
};

// Check if phone number already exists
export const checkPhoneExists = async (phone: string, excludeCustomerId?: string): Promise<Customer | null> => {
  try {
    const customersCollection = getCustomersCollection();

    // البحث عن رقم الهاتف الأساسي
    const phoneQuery = query(
      customersCollection,
      where('phone', '==', phone)
    );

    // البحث عن رقم الهاتف البديل
    const alternatePhoneQuery = query(
      customersCollection,
      where('alternatePhone', '==', phone)
    );

    const [phoneResults, alternatePhoneResults] = await Promise.all([
      getDocs(phoneQuery),
      getDocs(alternatePhoneQuery)
    ]);

    // التحقق من النتائج
    const allResults = [...phoneResults.docs, ...alternatePhoneResults.docs];

    for (const doc of allResults) {
      // إذا كان هناك معرف عميل مستثنى (للتحديث)، تجاهله
      if (excludeCustomerId && doc.id === excludeCustomerId) {
        continue;
      }

      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        branch: data.branch || '',
        notes: data.notes || '',
        alternatePhone: data.alternatePhone || '',
        devices: data.devices || [],
        totalDebt: data.totalDebt || 0,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastVisitDate: data.lastVisitDate?.toDate()
      } as Customer;
    }

    return null;
  } catch (error) {
    console.error('Error checking phone existence:', error);
    throw new Error('فشل في التحقق من رقم الهاتف');
  }
};

// Add a new customer
export const addCustomer = async (
  name: string,
  phone: string,
  email: string = '',
  address: string = '',
  branch: string = '',
  notes: string = '',
  alternatePhone: string = ''
): Promise<Customer> => {
  try {
    // التحقق من وجود رقم الهاتف الأساسي مسبقاً
    const existingCustomer = await checkPhoneExists(phone);
    if (existingCustomer) {
      throw new Error(`رقم الهاتف ${phone} مسجل مسبقاً باسم: ${existingCustomer.name}`);
    }

    // التحقق من وجود رقم الهاتف البديل مسبقاً (إذا تم إدخاله)
    if (alternatePhone && alternatePhone.trim() !== '') {
      const existingAlternateCustomer = await checkPhoneExists(alternatePhone);
      if (existingAlternateCustomer) {
        throw new Error(`رقم الهاتف البديل ${alternatePhone} مسجل مسبقاً باسم: ${existingAlternateCustomer.name}`);
      }
    }

    const customerData = {
      name,
      phone,
      email,
      address,
      branch,
      notes,
      alternatePhone,
      devices: [],
      totalDebt: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(collection(db, 'customers'), customerData);
    const newCustomer: Customer = {
      id: docRef.id,
      name,
      phone,
      email,
      address,
      branch,
      notes,
      alternatePhone,
      devices: [],
      totalDebt: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    return newCustomer;
  } catch (error) {
    console.error('Error adding customer:', error);
    // إذا كان الخطأ يحتوي على رسالة مخصصة، استخدمها
    if (error instanceof Error && error.message.includes('مسجل مسبقاً')) {
      throw error;
    }
    throw new Error('فشل في إضافة العميل');
  }
};

// Get a customer by ID
export const getCustomerById = async (customerId: string): Promise<Customer | null> => {
  try {
    const firestore = ensureFirestore();
    const customerDoc = await getDoc(doc(firestore, 'customers', customerId));

    if (customerDoc.exists()) {
      const customerData = customerDoc.data();
      return {
        id: customerDoc.id,
        name: customerData.name || '',
        phone: customerData.phone || '',
        alternatePhone: customerData.alternatePhone || '',
        ...customerData,
        createdAt: customerData.createdAt instanceof Timestamp ? customerData.createdAt.toDate() : new Date(),
        updatedAt: customerData.updatedAt instanceof Timestamp ? customerData.updatedAt.toDate() : new Date(),
        lastVisitDate: (customerData.lastVisit || customerData.lastVisitDate) instanceof Timestamp ?
          (customerData.lastVisit || customerData.lastVisitDate).toDate() : undefined,
        devices: customerData.devices || []
      } as Customer;
    }

    return null;
  } catch (error) {
    console.error('Error getting customer:', error);
    // إرجاع null بدلاً من رمي استثناء
    return null;
  }
};

// Get customer by email
export const getCustomerByEmail = async (email: string): Promise<Customer | null> => {
  try {
    const q = query(collection(db, 'customers'), where('email', '==', email));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || '',
        phone: data.phone || '',
        alternatePhone: data.alternatePhone || '',
        email: data.email || '',
        address: data.address || '',
        branch: data.branch || '',
        notes: data.notes || '',
        devices: data.devices || [],
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(),
        lastVisitDate: data.lastVisitDate instanceof Timestamp ? data.lastVisitDate.toDate() : undefined,
      } as Customer;
    }
    return null;
  } catch (error) {
    console.error('Error getting customer by email:', error);
    return null;
  }
};

// Get all customers with local storage cache
export const getAllCustomers = async (): Promise<Customer[]> => {
  try {
    const customersSnapshot = await getDocs(collection(db, 'customers'));
    return customersSnapshot.docs.map(doc => {
      const data = doc.data();
      const customer: Customer = {
        id: doc.id,
        name: data.name || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        branch: data.branch || '',
        notes: data.notes || '',
        alternatePhone: data.alternatePhone || '',
        devices: data.devices || [],
        totalDebt: data.totalDebt || 0,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastVisitDate: data.lastVisitDate?.toDate()
      };
      return customer;
    });
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw new Error('فشل في جلب بيانات العملاء');
  }
};

// Search customers by name or phone
export const searchCustomers = async (searchTerm: string): Promise<Customer[]> => {
  try {
    // إذا كان مصطلح البحث فارغًا، إرجاع مصفوفة فارغة
    if (!searchTerm || searchTerm.trim() === '') {
      return [];
    }

    // محاولة استخدام البيانات المخزنة محليًا للبحث
    const cachedData = localStorage.getItem('customers_cache');
    if (cachedData) {
      try {
        const customers = JSON.parse(cachedData);
        const searchTermLower = searchTerm.toLowerCase();

        // البحث في البيانات المخزنة محليًا
        const results = customers.filter((customer: Customer) =>
          customer.name.toLowerCase().includes(searchTermLower) ||
          customer.phone.includes(searchTerm) ||
          (customer.alternatePhone && customer.alternatePhone.includes(searchTerm))
        );

        if (results.length > 0) {
          console.log('Using cached data for customer search');
          return results;
        }
      } catch (cacheError) {
        console.warn('Error searching in cached customers data:', cacheError);
      }
    }

    // إذا لم تكن هناك بيانات مخزنة أو لم يتم العثور على نتائج، البحث في قاعدة البيانات
    // Firebase doesn't support complex text search, so we're doing a simple startsWith query
    const customersCollection = getCustomersCollection();

    // Combine results (removing duplicates)
    const resultsMap = new Map<string, Customer>();

    // Search by name (each query in its own try-catch so one failure doesn't break the other)
    try {
      const nameQuery = query(
        customersCollection,
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff')
      );
      const nameResults = await getDocs(nameQuery);
      nameResults.docs.forEach(doc => {
        const data = doc.data();
        resultsMap.set(doc.id, {
          id: doc.id,
          name: data.name || '',
          phone: data.phone || '',
          alternatePhone: data.alternatePhone || '',
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(),
          lastVisitDate: (data.lastVisit || data.lastVisitDate) instanceof Timestamp ?
            (data.lastVisit || data.lastVisitDate).toDate() : undefined,
          devices: data.devices || []
        } as Customer);
      });
    } catch (nameError) {
      console.warn('⚠️ Name search query failed:', nameError);
    }

    // Search by phone (separate try-catch so it doesn't break if missing composite index)
    try {
      const phoneQuery = query(
        customersCollection,
        where('phone', '>=', searchTerm),
        where('phone', '<=', searchTerm + '\uf8ff')
      );
      const phoneResults = await getDocs(phoneQuery);
      phoneResults.docs.forEach(doc => {
        if (!resultsMap.has(doc.id)) {
          const data = doc.data();
          resultsMap.set(doc.id, {
            id: doc.id,
            name: data.name || '',
            phone: data.phone || '',
            alternatePhone: data.alternatePhone || '',
            ...data,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(),
            lastVisitDate: (data.lastVisit || data.lastVisitDate) instanceof Timestamp ?
              (data.lastVisit || data.lastVisitDate).toDate() : undefined,
            devices: data.devices || []
          } as Customer);
        }
      });
    } catch (phoneError) {
      console.warn('⚠️ Phone search query failed (missing index?):', phoneError);
      // Fallback: fetch all customers and filter locally by phone
      try {
        const allCustomers = await getAllCustomers();
        allCustomers.forEach(c => {
          if (!resultsMap.has(c.id) && (
            c.phone.includes(searchTerm) ||
            (c.alternatePhone && c.alternatePhone.includes(searchTerm))
          )) {
            resultsMap.set(c.id, c);
          }
        });
      } catch (fallbackError) {
        console.warn('⚠️ Phone search fallback also failed:', fallbackError);
      }
    }

    return Array.from(resultsMap.values());
  } catch (error) {
    console.error('Error searching customers:', error);

    // في حالة الخطأ، محاولة البحث في البيانات المخزنة محليًا كملاذ أخير
    try {
      const cachedData = localStorage.getItem('customers_cache');
      if (cachedData) {
        const customers = JSON.parse(cachedData);
        const searchTermLower = searchTerm.toLowerCase();

        return customers.filter((customer: Customer) =>
          customer.name.toLowerCase().includes(searchTermLower) ||
          customer.phone.includes(searchTerm) ||
          (customer.alternatePhone && customer.alternatePhone.includes(searchTerm))
        );
      }
    } catch (fallbackError) {
      console.error('Error using cached data for customer search fallback:', fallbackError);
    }

    // إرجاع مصفوفة فارغة بدلاً من رمي استثناء
    return [];
  }
};

// Update a customer
export const updateCustomer = async (id: string, data: Partial<Customer>): Promise<void> => {
  try {
    const customerRef = doc(db, 'customers', id);
    await updateDoc(customerRef, {
      ...data,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating customer:', error);
    throw new Error('فشل في تحديث بيانات العميل');
  }
};

// Update customer's last visit
export const updateCustomerLastVisit = async (customerId: string): Promise<void> => {
  try {
    const firestore = ensureFirestore();
    const customerRef = doc(firestore, 'customers', customerId);
    await updateDoc(customerRef, {
      lastVisit: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating customer last visit:', error);
    throw error;
  }
};

// Delete a customer
export const deleteCustomer = async (id: string): Promise<void> => {
  try {
    const customerRef = doc(db, 'customers', id);
    await deleteDoc(customerRef);
  } catch (error) {
    console.error('Error deleting customer:', error);
    throw new Error('فشل في حذف العميل');
  }
};

// حذف جميع العملاء
export const deleteAllCustomers = async (): Promise<void> => {
  try {
    if (!db) {
      throw new Error('Firestore is not initialized');
    }

    const customersRef = collection(db, 'customers');
    const snapshot = await getDocs(customersRef);

    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    // حذف البيانات المخزنة محلياً
    localStorage.removeItem('customers_cache');
    localStorage.removeItem('customers_cache_expiry');

    console.log('All customers deleted successfully');
  } catch (error) {
    console.error('Error deleting all customers:', error);
    throw error;
  }
};